import {CareJourneyDetailsResponse, ScheduleAppointmentJourneyResponse, TaskStepName} from "@/api/dto/care-journey";
import {
  AppointmentCardWrapper,
  AppointmentContentItem,
  AppointmentContentSection,
  AppointmentContentSectionTitle,
  AppointmentContentTitle,
  AppointmentContentWrapper,
  TypeTitle
} from "@/components/pages/tasks/task_edit/progress/bh_journey/AppointmentCard.styles";
import {
  appointmentTypes,
  getAppointmentStatus
} from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep.constants";
import {StatusBadge} from "@/components/pages/tasks/task_edit/progress/CareNoteSection.styles";
import Spacer from "patient-ping-remedy/packages/spacer";
import {sizes} from "patient-ping-remedy/packages/theme";
import {StandardSemiBoldTypography, StandardTypography} from "patient-ping-remedy/packages/typography";
import DisplayHelpers from "@/helpers/display_helpers";
import React from "react";
import AvatarDetails from "@common/AvatarDetails";
import {aiAgentUser} from "@/components/pages/tasks/AssignedToTask";
import {User} from "@/api/dto/dto";
import {css} from "@emotion/css";
import {useTaskStepName} from "@/hooks/useTaskStepName";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import {useNavigate} from "react-router-dom";
import {SearchParam} from "@/helpers/search_params";
import {useActiveRoster} from "@/store/roster_store";
import {convertTimezoneToLocal, getTimezoneAbbreviation} from "@/helpers/timezone_helpers";

type Props = {
  parentJourney: CareJourneyDetailsResponse,
  appointment: ScheduleAppointmentJourneyResponse,
}

const AppointmentCard = (props: Props) => {
  const {taskStepDisplayValues} = useTaskStepName();
  const navigate = useNavigate();
  const currentRoster = useActiveRoster();
  const scheduledAttemptStep = props.appointment.steps?.find(step => step.stepName === TaskStepName.SCHEDULED_APPOINTMENT);
  const latestAttempt = scheduledAttemptStep?.attempts?.at(0);
  const attemptOwner = latestAttempt?.submittedBy as User;
  const collector = latestAttempt?.collector_type?.toLowerCase();
  const latestAttemptData = latestAttempt?.data;

  const searchParams = `${SearchParam.TASK_ID}=${props.parentJourney.careJourneyInstanceId}&` +
      `${SearchParam.ROSTER_ID}=${currentRoster?.id}&` +
      `${SearchParam.APPOINTMENT_ID}=${props.appointment.careJourneyInstanceId}`;
  
  const statusBadgeInfo = getAppointmentStatus(props.appointment);

  const appointmentTz = latestAttemptData?.appointment_timezone;
  const dateValue = latestAttemptData?.appointment_datetime || latestAttemptData?.date_of_attempt;
  let displayDate = appointmentTz && latestAttemptData?.appointment_datetime
    ? DisplayHelpers.formatDateTimeShort(convertTimezoneToLocal(latestAttemptData?.appointment_datetime, appointmentTz))
    : dateValue ? DisplayHelpers.formatDateTimeShort(dateValue) : '';

  if (appointmentTz) {
    const abbr = getTimezoneAbbreviation(appointmentTz);
    if (abbr) displayDate += ` (${abbr})`;
  }

  return <AppointmentCardWrapper
    id={`appointment-card-${props.appointment.careJourneyInstanceId}`}
    onClick={() => navigate(`/tasks/details/appointment?${searchParams}`)}
  >
    <div className={css({width: "95%"})}>
      <div className={css({marginBottom: sizes.xSmall})}>
        <TypeTitle>
          {appointmentTypes[props.appointment.providerType] ?? 'PCP'}
        </TypeTitle>
        <Spacer itemWidth={sizes.xSmall}/>
        <StatusBadge bgColor={statusBadgeInfo.backgroundColor}>
          <Icon
            iconClass={statusBadgeInfo.icon}
            color={statusBadgeInfo.iconColor}
            weight={"far"}
          />
          {statusBadgeInfo.status}
        </StatusBadge>
      </div>

      <AppointmentContentWrapper>
        <AppointmentContentSection width={'50%'}>
          <AppointmentContentItem>
            <AppointmentContentSectionTitle>Time: </AppointmentContentSectionTitle>
            <StandardTypography>
              {displayDate ? displayDate : 'N/A'}
            </StandardTypography>
          </AppointmentContentItem>

          <AppointmentContentItem>
            <AppointmentContentSectionTitle>Provider: </AppointmentContentSectionTitle>
            <div>
              {latestAttemptData?.scheduled_with ?? 'N/A'}
            </div>
          </AppointmentContentItem>

          <AppointmentContentItem>
            <AppointmentContentSectionTitle>Location: </AppointmentContentSectionTitle>
            <div>
              {latestAttemptData?.location ?? 'N/A'}
            </div>
          </AppointmentContentItem>

          <AppointmentContentItem>
            <AppointmentContentSectionTitle>Type: </AppointmentContentSectionTitle>
            <div>
              {latestAttemptData?.in_person_or_virtual ? DisplayHelpers.humanize(latestAttemptData.in_person_or_virtual) : 'N/A'}
            </div>
          </AppointmentContentItem>

          <AppointmentContentItem>
            <AppointmentContentSectionTitle>Phone number: </AppointmentContentSectionTitle>
            {latestAttemptData?.phone_number_to_location ?
              DisplayHelpers.formatPhoneNumber(latestAttemptData.phone_number_to_location) : 'N/A'}
          </AppointmentContentItem>
        </AppointmentContentSection>

        <AppointmentContentSection width={'48%'}>
          <AppointmentContentTitle>
            <StandardSemiBoldTypography>Last call by</StandardSemiBoldTypography>
          </AppointmentContentTitle>
          {latestAttempt ?
            collector === 'human'
              ? attemptOwner ?
                <AvatarDetails
                  user={attemptOwner}
                  description={`${DisplayHelpers.formatName(attemptOwner.first_name, null, attemptOwner.last_name)}
                    called at ${DisplayHelpers.formatDateTimeShort(latestAttempt.attempted_at)}`}
                /> : '' :
              <div className={css({display: 'flex'})}>
                <AvatarDetails
                  user={aiAgentUser}
                  description={`Bamboo AI called at ${DisplayHelpers.formatDateTimeShort(latestAttempt.attempted_at)}`}
                />

              </div>
            : <div className={css({height: sizes.small})}></div>
          } <br/>

          <AppointmentContentTitle>
            <StandardSemiBoldTypography>Current step</StandardSemiBoldTypography>
          </AppointmentContentTitle>
          <StandardTypography>{taskStepDisplayValues[props.appointment.currentStep]}</StandardTypography>

        </AppointmentContentSection>
      </AppointmentContentWrapper>

    </div>
    <AppointmentContentSection
      width={'5%'}
      className={css({display: "flex", flexDirection: "column", justifyContent: "center"})}
    >
      <Icon
        iconClass={ICONS["angle-right"]}
        iconSize={"3x"}
        weight={"fal"}
      />
    </AppointmentContentSection>
  </AppointmentCardWrapper>;
};

export default AppointmentCard;
